import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import styles from "./ContractorJobsPage.module.css";
import { JobDetailModal, DeclineReasonModal, TermsAndConditionsAcceptModal } from "./components";
import { useAuth } from "@/context/AuthContext";
import { jobsService } from "@/api/services/jobs.service";
import { documentsService } from "@/api/services/documents.service";
import { sitesService } from "@/api/services/sites.service";
import { TRADE_TYPE_LABELS, ENCUMBERED_TRADE_TYPES } from "@/types/job.types";
import { TradeDateChangeRequestModal } from "@/features/admin/jobs/components/TradeDateChangeRequestModal";
import { formatDateOnlyDisplay, getTimeFromIso, resolveDisplayTime, toDateOnly, toDateTimeLocal, } from "@/utils/dateTime";
import { ChangeDateModal } from "@/features/admin/jobs/components/ChangeDateModal";
import { useToast } from "@/components/common/Toast";
import { getBuildingPermitSlotsFromSiteDocuments } from "@/utils/buildingPermitDocuments";
import { buildFullTradeAssignmentPayload } from "@/utils/tradeAssignmentUtils";
import { LoopingImage } from "@/components/common/LoopingImage";
// Icons
const ClientIcon = () => (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 20 20", fill: "currentColor", children: _jsx("path", { d: "M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" }) }));
const ClockIcon = () => (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 20 20", fill: "currentColor", children: _jsx("path", { fillRule: "evenodd", d: "M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z", clipRule: "evenodd" }) }));
const LocationIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" }), _jsx("circle", { cx: "12", cy: "10", r: "3" })] }));
const CalendarIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2", ry: "2" }), _jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6" }), _jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6" }), _jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10" })] }));
const ToolIcon = () => (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" }) }));
const ChevronDownIcon = () => (_jsx("svg", { width: "24", height: "24", viewBox: "0 0 20 20", fill: "currentColor", children: _jsx("path", { fillRule: "evenodd", d: "M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z", clipRule: "evenodd" }) }));
/** Warning triangle icon + "Alert" text for "action required: change request pending". Clickable to open request date change popup. */
const ChangeRequestAlertIcon = ({ onClick, }) => (_jsxs("button", { type: "button", className: styles.changeRequestAlertIcon, onClick: onClick, "aria-label": "Action required: change request pending", children: [_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" }), _jsx("line", { x1: "12", y1: "9", x2: "12", y2: "13" }), _jsx("line", { x1: "12", y1: "17", x2: "12.01", y2: "17" })] }), _jsx("span", { children: "Alert" })] }));
/** Clock icon + "Pending" text when contractor has submitted a date change request (awaiting approval). */
const DateChangePendingBadge = () => (_jsxs("span", { className: styles.dateChangePendingBadge, "aria-label": "Date change request pending", children: [_jsx("svg", { width: "16", height: "16", viewBox: "0 0 20 20", fill: "currentColor", children: _jsx("path", { fillRule: "evenodd", d: "M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z", clipRule: "evenodd" }) }), _jsx("span", { children: "Pending" })] }));
const LoadingSpinner = () => (_jsxs("div", { className: styles.loadingContainer, children: [_jsx("div", { className: styles.spinner }), _jsx("p", { children: "Loading jobs..." })] }));
const formatDate = (dateString) => {
    return formatDateOnlyDisplay(dateString);
};
const formatTimeRange = (startTime, endTime) => {
    if (!startTime && !endTime)
        return "TBD";
    return `${startTime || "TBD"} - ${endTime || "TBD"}`;
};
const formatDuration = (days) => {
    if (!days)
        return "TBD";
    return days === 1 ? "1 Day" : `${days} Days`;
};
const getTradeTypeLabel = (tradeType) => {
    return (TRADE_TYPE_LABELS[tradeType] ||
        tradeType.toUpperCase());
};
// Document type → display label (BUILDING SURVEYOR pattern)
const DOCUMENT_TYPE_LABELS = {
    site_map: "Site Map",
    notice_to_vacate: "Notice to Vacate (SOW)",
    certificates: "Certificate",
    other: "Other",
};
/** Fetches tenancy string (tenancyNumber, tenancyName, shoppingCenter) and full site address from job → site. */
const fetchSiteMetaForJob = async (jobId) => {
    try {
        const jobRes = await jobsService.getJob(jobId);
        const siteId = jobRes.data?.siteId ?? null;
        if (!siteId)
            return null;
        const siteRes = await sitesService.getSite(siteId);
        const site = siteRes.data;
        if (!site)
            return null;
        const tenancyNumber = site.tenancyNumber ?? "";
        const tenancyName = site.tenancyName ?? "";
        const shoppingCenter = site.contactPerson?.shoppingCenter ?? "";
        const tenancyParts = [tenancyNumber, tenancyName, shoppingCenter].filter(Boolean);
        const address = site.address ?? "";
        const suburb = site.suburb ?? "";
        const state = site.state ?? "";
        const postcode = site.postcode ?? "";
        const addressParts = [address, suburb, state, postcode].filter(Boolean);
        return {
            tenancy: tenancyParts.length > 0 ? tenancyParts.join(", ") : null,
            fullAddress: addressParts.length > 0 ? addressParts.join(", ") : null,
        };
    }
    catch {
        return null;
    }
};
const isEncumberedTrade = (tradeType) => ENCUMBERED_TRADE_TYPES.includes(tradeType);
/** Map contractor assignment to shape required by TradeDateChangeRequestModal */
function toTradeAssignmentForModal(job) {
    return {
        id: job.id,
        jobId: job.jobId,
        tradeType: job.tradeType,
        tradespersonId: null,
        tradespersonName: null,
        isEncumbered: job.isEncumbered ?? false,
        duration: job.duration,
        commencementDate: job.commencementDate,
        commencementTime: job.commencementTime,
        completionDate: job.completionDate,
        completionTime: job.completionTime,
        status: job.status,
        statusNote: null,
        didNotShow: null,
        reason: null,
        neatJob: null,
        createdAt: job.createdAt,
        updatedAt: job.createdAt,
        confirmedAt: job.confirmedAt,
        declinedAt: job.declinedAt,
    };
}
const MyJobCard = ({ job, index, isEncumbered, tenancyDetails, fullAddress, onViewJob, onRequestChange, onCancellationRequest, onAlertClick, isDetailOpen, selectedJobData, onCloseDetails, onDecline, isProcessing, pendingTradeDateChangeRequest, onViewTradeDateChangeDetails, hasPendingContractorDateChange, }) => {
    const startTimeResolved = resolveDisplayTime(job.commencementDate, job.commencementTime, job.startTime) || getTimeFromIso(job.commencementDate) || "";
    const endTimeResolved = resolveDisplayTime(job.completionDate, job.completionTime, job.endTime) || getTimeFromIso(job.completionDate) || "";
    return (_jsxs("div", { className: styles.myJobCardWrapper, children: [_jsxs("article", { className: styles.myJobCard, children: [_jsx("div", { className: styles.myJobImage, children: job.imageUrl ? (_jsx("img", { src: job.imageUrl, alt: `Job ${job.jobNumber}`, onError: (e) => {
                                e.currentTarget.src =
                                    "/images/Defit Social Media 3.png";
                            } })) : (_jsx(LoopingImage, { imagePrefix: "/images/request-quote-default", count: index + 1, fallbackImage: "/images/empty-state-job.png", className: styles.myJobLoopingImage })) }), _jsxs("div", { className: styles.myJobDetails, children: [_jsxs("div", { className: styles.myJobHeader, children: [_jsxs("div", { className: styles.myJobTitleRow, children: [_jsxs("span", { className: styles.myJobTitle, children: ["Job #", job.jobNumber] }), job.hasPendingChangeRequest && (_jsx(ChangeRequestAlertIcon, { onClick: () => onAlertClick?.(job.id) })), hasPendingContractorDateChange && (_jsx(DateChangePendingBadge, {}))] }), _jsx("span", { className: styles.myJobClient, children: job.clientName || job.jobTitle })] }), _jsxs("div", { className: styles.myJobInfoGrid, children: [tenancyDetails && (_jsxs("div", { className: styles.myJobInfoItem, children: [_jsx(LocationIcon, {}), _jsxs("span", { children: ["Tenancy: ", tenancyDetails] })] })), _jsxs("div", { className: styles.myJobInfoItem, children: [_jsx(LocationIcon, {}), _jsx("span", { children: fullAddress || job.siteAddress || job.location || "TBD" })] }), _jsxs("div", { className: styles.myJobInfoItem, children: [_jsx(CalendarIcon, {}), _jsx("span", { children: formatDate(job.commencementDate) })] }), _jsxs("div", { className: styles.myJobInfoItem, children: [_jsx(ClockIcon, {}), _jsx("span", { children: formatTimeRange(startTimeResolved, endTimeResolved) })] }), !isEncumbered && (_jsxs("div", { className: styles.myJobInfoItem, children: [_jsx(ClockIcon, {}), _jsx("span", { children: formatDuration(job.duration) })] }))] }), _jsxs("div", { className: clsx(styles.myJobActions, isEncumbered && styles.myJobActionsEncumbered), children: [_jsxs("button", { className: styles.viewJobButton, onClick: () => onViewJob(job.id), children: [_jsx(ToolIcon, {}), "VIEW JOB"] }), _jsx("button", { className: styles.requestChangeButton, onClick: () => onRequestChange(job.id), disabled: hasPendingContractorDateChange, children: hasPendingContractorDateChange ? "Request Pending" : "REQUEST CHANGE" }), !isEncumbered && (_jsx("button", { className: styles.requestChangeButton, onClick: () => onCancellationRequest(job.id), children: "CANCELLATION REQUEST" }))] })] })] }), isDetailOpen && selectedJobData && (_jsx(JobDetailModal, { isOpen: true, onClose: onCloseDetails, job: selectedJobData, onDecline: onDecline, isProcessing: isProcessing, hideAcceptButton: true, declineButtonLabel: isEncumbered ? "Request Change" : "Cancellation Request", hideDuration: isEncumbered, pendingTradeDateChangeRequest: pendingTradeDateChangeRequest ?? null, onViewTradeDateChangeDetails: onViewTradeDateChangeDetails, jobId: job.jobId }))] }));
};
const JobCard = ({ job, isEncumbered, tenancyDetails, fullAddress, onDetails, onAccept, onDecline, isDetailOpen, selectedJobData, onCloseDetails, isProcessing, onRequestDateChange, onAlertClick, pendingTradeDateChangeRequest, onViewTradeDateChangeDetails, hasPendingContractorDateChange, }) => {
    return (_jsxs("div", { className: styles.jobCardWrapper, children: [_jsxs("div", { className: styles.jobCard, children: [_jsxs("div", { className: styles.jobCardHeader, children: [_jsx("span", { className: styles.categoryBadge, children: getTradeTypeLabel(job.tradeType) }), _jsxs("span", { className: styles.jobCardHeaderRight, children: [!isEncumbered && (_jsx("span", { className: styles.hoursLeftBadge, children: formatDuration(job.duration) })), job.hasPendingChangeRequest && (_jsx(ChangeRequestAlertIcon, { onClick: () => onAlertClick?.(job.id) })), hasPendingContractorDateChange && (_jsx(DateChangePendingBadge, {}))] })] }), _jsxs("h3", { className: styles.jobNumber, children: ["Job #", job.jobNumber] }), _jsxs("div", { className: styles.jobDetails, children: [_jsxs("div", { className: styles.jobDetailItem, children: [_jsx(ClientIcon, {}), _jsxs("span", { children: ["Client: ", job.clientName || "TBD"] })] }), _jsxs("div", { className: styles.jobDetailItem, children: [_jsx(CalendarIcon, {}), _jsxs("span", { children: ["Start: ", formatDate(job.commencementDate), ",", " ", resolveDisplayTime(job.commencementDate, job.commencementTime, job.startTime) ||
                                                getTimeFromIso(job.commencementDate) ||
                                                "TBD"] })] }), _jsxs("div", { className: styles.jobDetailItem, children: [_jsx(LocationIcon, {}), _jsx("span", { children: fullAddress || job.siteAddress || job.location || "TBD" })] }), tenancyDetails && (_jsxs("div", { className: styles.jobDetailItem, children: [_jsx(LocationIcon, {}), _jsxs("span", { children: ["Tenancy Details: ", tenancyDetails] })] }))] }), _jsx("p", { className: styles.jobDescription, children: job.description || job.scope || "No description available" }), _jsxs("div", { className: styles.jobActions, children: [_jsx("button", { className: styles.detailsButton, onClick: () => onDetails(job.id), disabled: isProcessing, children: "DETAILS" }), _jsx("button", { className: styles.acceptButton, onClick: () => onAccept(job.id), disabled: isProcessing, children: isProcessing ? "..." : "ACCEPT" }), !isEncumbered && (_jsx("button", { className: styles.declineButton, onClick: () => onDecline(job.id), disabled: isProcessing, children: isProcessing ? "..." : "DECLINE" })), onRequestDateChange && (_jsx("button", { className: styles.requestChangeButton, onClick: () => onRequestDateChange(job.id), disabled: isProcessing || hasPendingContractorDateChange, children: hasPendingContractorDateChange
                                    ? "Request Pending"
                                    : "REQUEST DATE CHANGE" }))] })] }), isDetailOpen && selectedJobData && (_jsx(JobDetailModal, { isOpen: true, onClose: onCloseDetails, job: selectedJobData, onAccept: onAccept, onDecline: onDecline, isProcessing: isProcessing, hideDeclineButton: isEncumbered, hideDuration: isEncumbered, pendingTradeDateChangeRequest: pendingTradeDateChangeRequest ?? null, onViewTradeDateChangeDetails: onViewTradeDateChangeDetails }))] }));
};
/** Map trade date change request to DateChangeRequest shape for ChangeDateModal.
 * API returns datetime in date fields (YYYY-MM-DDTHH:mm:ss); pass as-is. Backward compat: if date-only, concatenate with time. */
function tradeDateChangeRequestToDisplay(req) {
    const toDisplayDate = (dateVal, timeVal) => {
        if (!dateVal)
            return null;
        if (dateVal.includes("T"))
            return dateVal;
        const built = toDateTimeLocal(toDateOnly(dateVal), timeVal ?? "12:00");
        return built || null;
    };
    return {
        id: req.id,
        jobId: req.jobId,
        previousDuration: 0,
        previousCommencementDate: toDisplayDate(req.previousCommencementDate, req.previousCommencementTime),
        previousCompletionDate: toDisplayDate(req.previousCompletionDate, req.previousCompletionTime),
        newDuration: 0,
        newCommencementDate: toDisplayDate(req.newCommencementDate, req.newCommencementTime),
        newCompletionDate: toDisplayDate(req.newCompletionDate, req.newCompletionTime),
        reason: req.reason,
        status: "pending",
        requestor: req.requestor,
        requestedById: req.requestedById,
        requestedByName: req.requestedByName,
        approvedById: req.approvedById,
        approvedByName: req.approvedByName,
        createdAt: req.createdAt,
        approvedAt: req.approvedAt,
        declinedAt: req.declinedAt,
    };
}
export const ContractorJobsPage = () => {
    const { user } = useAuth();
    const toast = useToast();
    const navigate = useNavigate();
    const [assignments, setAssignments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const [isNewJobsExpanded, setIsNewJobsExpanded] = useState(true);
    const [isMyJobsExpanded, setIsMyJobsExpanded] = useState(true);
    const [selectedJobId, setSelectedJobId] = useState(null);
    const [selectedJob, setSelectedJob] = useState(null);
    /** Merged documents (assignment + site building-permit docs) for detail modal; null until fetched. */
    const [detailDocuments, setDetailDocuments] = useState(null);
    const [declineModalAssignmentId, setDeclineModalAssignmentId] = useState(null);
    const [declineModalPurpose, setDeclineModalPurpose] = useState(null);
    /** Tenancy Details string for detail modal (Tenancy Number, Tenancy Name, Shopping Center). */
    const [tenancyDetails, setTenancyDetails] = useState(null);
    /** Site meta (tenancy + full address) by jobId for display on cards (populated when assignments load). */
    const [siteMetaByJobId, setSiteMetaByJobId] = useState({});
    /** Job id when detail is open (for refetch keys). */
    const [detailJobId, setDetailJobId] = useState(null);
    /** Trade date change requests for detail modal (from GET /jobs/{id}/trade-date-change-requests). */
    const [tradeDateChangeRequestsForDetail, setTradeDateChangeRequestsForDetail] = useState([]);
    const [isTradeDateChangeModalOpen, setIsTradeDateChangeModalOpen] = useState(false);
    /** Assignment selected for Request Change (Trade Dates) modal — from My Jobs encumbered or New Job Update DEFIT */
    const [tradeDateChangeRequestJob, setTradeDateChangeRequestJob] = useState(null);
    /** Assignment id when T&C accept modal is open; null when closed. */
    const [acceptTcModalAssignmentId, setAcceptTcModalAssignmentId] = useState(null);
    /** When true, open ChangeDateModal once trade date change requests load (after alert icon click). */
    const [openChangeRequestModalOnDetailLoad, setOpenChangeRequestModalOnDetailLoad] = useState(false);
    /** Pending contractor-requested trade date changes by assignment ID (for card badges). */
    const [pendingContractorRequestsByAssignmentId, setPendingContractorRequestsByAssignmentId] = useState({});
    // Split assignments into pending (new job updates) and confirmed (my jobs)
    // Guard: API may return wrapped object instead of array
    const assignmentList = Array.isArray(assignments) ? assignments : [];
    const pendingJobs = assignmentList
        .filter((a) => a.status === "pending")
        .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
    const myJobs = assignmentList
        .filter((a) => a.status === "confirmed")
        .sort((a, b) => {
        const da = a.commencementDate ?? "";
        const db = b.commencementDate ?? "";
        if (!da)
            return 1;
        if (!db)
            return -1;
        return da.localeCompare(db);
    });
    // Fetch assignments on mount
    // For contractors, the user.id IS the contact ID (from JWT payload sub field)
    const contractorId = user?.id;
    const fetchAssignments = useCallback(async () => {
        if (!contractorId) {
            setError("No contractor ID found. Please log in again.");
            setIsLoading(false);
            return;
        }
        try {
            setIsLoading(true);
            setError(null);
            const response = await jobsService.getTradeAssignmentsByContractor(contractorId);
            const data = response.data;
            let list = [];
            if (Array.isArray(data)) {
                list = data;
            }
            else if (data && typeof data === "object") {
                const wrapped = data;
                const arr = wrapped.assignments ?? wrapped.data ?? wrapped.items;
                list = Array.isArray(arr) ? arr : [];
            }
            setAssignments(list);
        }
        catch (err) {
            console.error("Failed to fetch assignments:", err);
            setError("Failed to load jobs. Please try again.");
        }
        finally {
            setIsLoading(false);
        }
    }, [contractorId]);
    useEffect(() => {
        fetchAssignments();
    }, [fetchAssignments]);
    // Fetch tenancy for all assignments when list loads (for card display)
    useEffect(() => {
        const jobIds = [...new Set(assignmentList.map((a) => a.jobId))];
        if (jobIds.length === 0)
            return;
        let cancelled = false;
        const CONCURRENCY = 4;
        const run = async () => {
            const next = {};
            for (let i = 0; i < jobIds.length; i += CONCURRENCY) {
                if (cancelled)
                    break;
                const batch = jobIds.slice(i, i + CONCURRENCY);
                const results = await Promise.all(batch.map((id) => fetchSiteMetaForJob(id)));
                batch.forEach((id, idx) => {
                    next[id] = results[idx];
                });
            }
            if (!cancelled) {
                setSiteMetaByJobId((prev) => ({ ...prev, ...next }));
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [assignmentList]);
    // Fetch trade date change requests for all jobs to show contractor-requested pending state on cards
    useEffect(() => {
        const jobIds = [...new Set(assignmentList.map((a) => a.jobId))];
        if (jobIds.length === 0) {
            setPendingContractorRequestsByAssignmentId({});
            return;
        }
        let cancelled = false;
        const CONCURRENCY = 4;
        const run = async () => {
            const map = {};
            for (let i = 0; i < jobIds.length; i += CONCURRENCY) {
                if (cancelled)
                    break;
                const batch = jobIds.slice(i, i + CONCURRENCY);
                const results = await Promise.all(batch.map((id) => jobsService.getTradeDateChangeRequests(id).then((res) => {
                    const arr = Array.isArray(res.data)
                        ? res.data
                        : res.data?.data ?? [];
                    return arr;
                })));
                batch.forEach((_jobId, idx) => {
                    const requests = results[idx] ?? [];
                    for (const req of requests) {
                        if (String(req?.requestor ?? "").toUpperCase() === "CONTRACTOR" &&
                            String(req?.status ?? "").toUpperCase() === "PENDING") {
                            const assignmentId = req.tradeAssignmentId ?? req.jobTradeId ?? null;
                            if (assignmentId && !map[assignmentId]) {
                                map[assignmentId] = req;
                            }
                        }
                    }
                });
            }
            if (!cancelled) {
                setPendingContractorRequestsByAssignmentId(map);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [assignmentList]);
    // Fetch job, tenancy details (site), and site documents when detail is open
    useEffect(() => {
        if (!selectedJob?.jobId) {
            setDetailDocuments(null);
            setTenancyDetails(null);
            setDetailJobId(null);
            setTradeDateChangeRequestsForDetail([]);
            return;
        }
        let cancelled = false;
        const run = async () => {
            setDetailDocuments(null);
            setTenancyDetails(null);
            setTradeDateChangeRequestsForDetail([]);
            try {
                const [jobRes, tdcrRes] = await Promise.all([
                    jobsService.getJob(selectedJob.jobId),
                    jobsService.getTradeDateChangeRequests(selectedJob.jobId),
                ]);
                const job = jobRes.data;
                if (cancelled)
                    return;
                if (!cancelled && job) {
                    setDetailJobId(job.id);
                }
                const tdcrList = Array.isArray(tdcrRes.data)
                    ? tdcrRes.data
                    : tdcrRes.data?.data ??
                        [];
                if (!cancelled) {
                    setTradeDateChangeRequestsForDetail(tdcrList ?? []);
                }
                const siteId = job?.siteId ?? null;
                // Tenancy details: site (tenancyNumber, tenancyName, contactPerson?.shoppingCenter)
                let tenancyNumber = "";
                let tenancyName = "";
                let shoppingCenter = "";
                if (siteId) {
                    try {
                        const siteRes = await sitesService.getSite(siteId);
                        if (!cancelled && siteRes.data) {
                            tenancyNumber = siteRes.data.tenancyNumber ?? "";
                            tenancyName = siteRes.data.tenancyName ?? "";
                            shoppingCenter = siteRes.data.contactPerson?.shoppingCenter ?? "";
                        }
                    }
                    catch {
                        // ignore
                    }
                }
                const tenancyParts = [
                    tenancyNumber,
                    tenancyName,
                    shoppingCenter,
                ].filter(Boolean);
                if (!cancelled) {
                    setTenancyDetails(tenancyParts.length > 0 ? tenancyParts.join(", ") : null);
                }
                // Base documents from assignment (Certificate excluded from display later)
                const baseDocs = selectedJob.documents.map((doc) => {
                    const raw = (doc.category ??
                        doc.documentType ??
                        "other")
                        .toString()
                        .toLowerCase();
                    const label = (DOCUMENT_TYPE_LABELS[raw] ?? "Other");
                    const fileUrl = doc.fileUrl ?? doc.storageUrl ?? "";
                    return {
                        id: doc.id,
                        category: label,
                        fileName: doc.fileName ??
                            doc.name ??
                            "Document",
                        fileUrl: typeof fileUrl === "string" ? fileUrl : "",
                    };
                });
                const slotOrderNoCert = ["Site Map", "Notice to Vacate (SOW)", "Other"];
                const baseDocsNoCert = baseDocs.filter((d) => d.category !== "Certificate");
                if (!siteId) {
                    if (!cancelled)
                        setDetailDocuments(baseDocsNoCert);
                    return;
                }
                const siteRes = await documentsService.getDocumentsBySite(siteId);
                const siteDocuments = siteRes.data ?? [];
                if (cancelled)
                    return;
                const buildingPermitSlots = getBuildingPermitSlotsFromSiteDocuments(siteDocuments);
                const slotKeys = {
                    "Site Map": "site_map",
                    "Notice to Vacate (SOW)": "notice_to_vacate",
                    Other: "other",
                };
                const merged = [];
                for (const cat of slotOrderNoCert) {
                    const docs = buildingPermitSlots[slotKeys[cat]] ?? [];
                    if (docs.length > 0) {
                        for (const siteDoc of docs) {
                            merged.push({
                                id: siteDoc.id,
                                category: cat,
                                fileName: siteDoc.name || siteDoc.fileName,
                                fileUrl: "",
                                source: "site",
                            });
                        }
                    }
                    else {
                        merged.push(...baseDocsNoCert.filter((d) => d.category === cat));
                    }
                }
                merged.push(...baseDocsNoCert.filter((d) => !slotOrderNoCert.includes(d.category)));
                if (!cancelled)
                    setDetailDocuments(merged);
            }
            catch (err) {
                console.error("Failed to fetch job/site documents for detail:", err);
                if (!cancelled) {
                    setDetailDocuments(null);
                    setTenancyDetails(null);
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [selectedJob?.jobId, selectedJob?.documents]);
    const handleDetails = (assignmentId) => {
        const job = assignmentList.find((j) => j.id === assignmentId);
        if (job) {
            setSelectedJobId(assignmentId);
            setSelectedJob(job);
        }
    };
    const handleViewJob = (assignmentId) => {
        handleDetails(assignmentId);
    };
    const handleCloseDetails = () => {
        setSelectedJobId(null);
        setDetailDocuments(null);
        setTenancyDetails(null);
        setDetailJobId(null);
        setTradeDateChangeRequestsForDetail([]);
        setIsTradeDateChangeModalOpen(false);
        setOpenChangeRequestModalOnDetailLoad(false);
        setTimeout(() => setSelectedJob(null), 300);
    };
    const handleAccept = async (assignmentId, options) => {
        if (!contractorId)
            return;
        try {
            setProcessingId(assignmentId);
            await jobsService.contractorAcceptAssignment(assignmentId, contractorId);
            if (options?.termCondition === true) {
                const assignment = assignmentList.find((a) => a.id === assignmentId);
                if (assignment) {
                    const res = await jobsService.getTradeAssignments(assignment.jobId);
                    const fullAssignment = Array.isArray(res.data)
                        ? res.data.find((a) => a.id === assignmentId)
                        : null;
                    if (fullAssignment) {
                        const fullPayload = buildFullTradeAssignmentPayload(fullAssignment, { termCondition: true });
                        await jobsService.updateTradeAssignment(assignmentId, fullPayload);
                    }
                }
            }
            await fetchAssignments();
            handleCloseDetails();
        }
        catch (err) {
            console.error("Failed to accept assignment:", err);
            setError("Failed to accept job. Please try again.");
        }
        finally {
            setProcessingId(null);
        }
    };
    /** Opens T&C modal instead of accepting immediately; user must confirm in modal. */
    const handleAcceptClick = (assignmentId) => {
        setAcceptTcModalAssignmentId(assignmentId);
    };
    const openDeclineModal = (assignmentId) => {
        setDeclineModalAssignmentId(assignmentId);
        setDeclineModalPurpose("decline");
    };
    const openCancellationModal = (assignmentId) => {
        setDeclineModalAssignmentId(assignmentId);
        setDeclineModalPurpose("cancellation");
    };
    const handleCloseDeclineModal = useCallback(() => {
        setDeclineModalAssignmentId(null);
        setDeclineModalPurpose(null);
    }, []);
    const handleDeclineWithReason = async (reason) => {
        if (!contractorId || !declineModalAssignmentId)
            return;
        try {
            setProcessingId(declineModalAssignmentId);
            await jobsService.contractorDeclineAssignment(declineModalAssignmentId, contractorId, reason);
            await fetchAssignments();
        }
        catch (err) {
            console.error("Failed to decline assignment:", err);
            setError("Failed to decline job. Please try again.");
        }
        finally {
            handleCloseDeclineModal();
            handleCloseDetails();
            setProcessingId(null);
        }
    };
    const handleRequestChange = (assignmentId) => {
        const job = myJobs.find((j) => j.id === assignmentId);
        if (job)
            setTradeDateChangeRequestJob(job);
    };
    const handleRequestDateChange = (assignmentId) => {
        const job = pendingJobs.find((j) => j.id === assignmentId);
        if (job)
            setTradeDateChangeRequestJob(job);
    };
    const handleCancellationRequest = (assignmentId) => {
        openCancellationModal(assignmentId);
    };
    const handleAlertClick = (assignmentId) => {
        const job = assignmentList.find((j) => j.id === assignmentId);
        if (job) {
            setSelectedJob(job);
            setSelectedJobId(null); // Don't open View Job modal
            setOpenChangeRequestModalOnDetailLoad(true);
        }
    };
    const pendingTradeDateChangeRequestStaff = tradeDateChangeRequestsForDetail.find((r) => String(r?.status ?? "").toUpperCase() === "PENDING" &&
        r.requestor === "Staff");
    // Open ChangeDateModal when alert icon was clicked and trade date change requests have loaded
    useEffect(() => {
        if (!openChangeRequestModalOnDetailLoad ||
            !pendingTradeDateChangeRequestStaff)
            return;
        setIsTradeDateChangeModalOpen(true);
        setOpenChangeRequestModalOnDetailLoad(false);
    }, [
        openChangeRequestModalOnDetailLoad,
        pendingTradeDateChangeRequestStaff,
    ]);
    const handleApproveTradeDateChange = async () => {
        if (!pendingTradeDateChangeRequestStaff)
            return;
        const jobIdToRefetch = selectedJob?.jobId ?? detailJobId;
        try {
            await jobsService.processTradeDateChangeRequest(pendingTradeDateChangeRequestStaff.id, {
                action: "approved",
                approvedById: user?.id ?? "",
                approvedByName: user?.name ?? "",
            });
            await fetchAssignments();
            if (jobIdToRefetch) {
                const tdcrRes = await jobsService.getTradeDateChangeRequests(jobIdToRefetch);
                const list = Array.isArray(tdcrRes.data)
                    ? tdcrRes.data
                    : tdcrRes.data?.data ??
                        [];
                setTradeDateChangeRequestsForDetail(list ?? []);
            }
            toast.success("Trade date change approved.", 3000);
            setIsTradeDateChangeModalOpen(false);
            handleCloseDetails();
        }
        catch (err) {
            toast.error("Failed to approve trade date change.", 3000);
        }
    };
    const handleDeclineTradeDateChange = async () => {
        if (!pendingTradeDateChangeRequestStaff)
            return;
        const jobIdToRefetch = selectedJob?.jobId ?? detailJobId;
        try {
            await jobsService.processTradeDateChangeRequest(pendingTradeDateChangeRequestStaff.id, {
                action: "declined",
                approvedById: user?.id ?? "",
                approvedByName: user?.name ?? "",
            });
            await fetchAssignments();
            if (jobIdToRefetch) {
                const tdcrRes = await jobsService.getTradeDateChangeRequests(jobIdToRefetch);
                const list = Array.isArray(tdcrRes.data)
                    ? tdcrRes.data
                    : tdcrRes.data?.data ??
                        [];
                setTradeDateChangeRequestsForDetail(list ?? []);
            }
            toast.success("Trade date change declined.", 3000);
            setIsTradeDateChangeModalOpen(false);
            handleCloseDetails();
        }
        catch (err) {
            toast.error("Failed to decline trade date change.", 3000);
        }
    };
    const handleCommunicateWithStaff = () => {
        setIsTradeDateChangeModalOpen(false);
        navigate("/contractor/messages");
    };
    // Transform job data for the detail modal
    const getSelectedJobData = (job) => {
        if (!job)
            return null;
        const docWithType = (doc) => {
            const raw = (doc.category ??
                doc.documentType ??
                "other")
                .toString()
                .toLowerCase();
            const label = (DOCUMENT_TYPE_LABELS[raw] ?? "Other");
            const fileUrl = doc.fileUrl ?? doc.storageUrl ?? "";
            return {
                id: doc.id,
                category: label,
                fileName: doc.fileName ?? doc.name ?? "Document",
                fileUrl: typeof fileUrl === "string" ? fileUrl : "",
            };
        };
        const startDateFormatted = job.commencementDate
            ? formatDate(job.commencementDate)
            : "";
        const startTimeResolved = resolveDisplayTime(job.commencementDate, job.commencementTime, job.startTime) || getTimeFromIso(job.commencementDate) || "";
        const endDateFormatted = job.completionDate
            ? formatDate(job.completionDate)
            : "";
        const endTimeResolved = resolveDisplayTime(job.completionDate, job.completionTime, job.endTime) || getTimeFromIso(job.completionDate) || "";
        const fullAddressForDetail = (job.jobId && siteMetaByJobId[job.jobId]?.fullAddress) || null;
        return {
            id: job.id,
            jobNumber: job.jobNumber,
            jobName: job.jobTitle || job.jobNumber || "Job",
            siteAddress: fullAddressForDetail || job.siteAddress || job.location || "TBD",
            jobScope: job.scope || job.description || "",
            durationDays: job.duration || 1,
            startDate: startDateFormatted,
            startTime: startTimeResolved,
            endDate: endDateFormatted,
            endTime: endTimeResolved,
            jobDescription: job.description || "",
            documents: job.documents.map(docWithType),
        };
    };
    const isMyJobDetailOpen = selectedJobId != null &&
        selectedJob != null &&
        myJobs.some((j) => j.id === selectedJobId);
    /** Job data for detail modal; uses merged documents (site building-permit slots) when loaded. */
    const detailJobData = selectedJob != null
        ? (() => {
            const base = getSelectedJobData(selectedJob);
            if (!base)
                return null;
            return {
                ...base,
                documents: detailDocuments ?? base.documents,
                tenancyDetails: tenancyDetails ?? undefined,
            };
        })()
        : null;
    useEffect(() => {
        if (!isMyJobDetailOpen)
            return;
        const onKeyDown = (e) => {
            if (e.key === "Escape")
                handleCloseDetails();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isMyJobDetailOpen]);
    if (isLoading) {
        return _jsx(LoadingSpinner, {});
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.container, children: [error && (_jsxs("div", { className: styles.errorBanner, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchAssignments, children: "Retry" })] })), _jsxs("section", { className: styles.section, children: [_jsxs("button", { className: styles.sectionHeader, onClick: () => setIsNewJobsExpanded(!isNewJobsExpanded), children: [_jsx("h2", { className: styles.sectionTitle, children: "New Job Update" }), _jsx("span", { className: `${styles.chevron} ${isNewJobsExpanded ? styles.chevronExpanded : ""}`, children: _jsx(ChevronDownIcon, {}) })] }), isNewJobsExpanded && (_jsx("div", { className: styles.jobsList, children: pendingJobs.length > 0 ? (pendingJobs.map((job) => (_jsx(JobCard, { job: job, isEncumbered: job.isEncumbered ?? isEncumberedTrade(job.tradeType), tenancyDetails: siteMetaByJobId[job.jobId]?.tenancy ?? null, fullAddress: siteMetaByJobId[job.jobId]?.fullAddress ?? null, onDetails: handleDetails, onAccept: handleAcceptClick, onDecline: openDeclineModal, isDetailOpen: selectedJobId === job.id, selectedJobData: selectedJobId === job.id ? detailJobData : null, onCloseDetails: handleCloseDetails, isProcessing: processingId === job.id, onRequestDateChange: handleRequestDateChange, onAlertClick: handleAlertClick, pendingTradeDateChangeRequest: selectedJobId === job.id
                                        ? pendingTradeDateChangeRequestStaff ?? null
                                        : null, onViewTradeDateChangeDetails: selectedJobId === job.id
                                        ? () => setIsTradeDateChangeModalOpen(true)
                                        : undefined, hasPendingContractorDateChange: !!pendingContractorRequestsByAssignmentId[job.id] }, job.id)))) : (_jsx("div", { className: styles.emptyState, children: _jsx("p", { className: styles.emptyText, children: "No new job requests" }) })) }))] }), _jsxs("section", { className: styles.section, children: [_jsxs("button", { className: styles.sectionHeader, onClick: () => setIsMyJobsExpanded(!isMyJobsExpanded), children: [_jsx("h2", { className: styles.sectionTitle, children: "My Jobs" }), _jsx("span", { className: `${styles.chevron} ${isMyJobsExpanded ? styles.chevronExpanded : ""}`, children: _jsx(ChevronDownIcon, {}) })] }), isMyJobsExpanded && (_jsx("div", { className: styles.myJobsList, children: myJobs.length > 0 ? (myJobs.map((job, i) => (_jsx(MyJobCard, { job: job, index: i, isEncumbered: job.isEncumbered ?? isEncumberedTrade(job.tradeType), tenancyDetails: siteMetaByJobId[job.jobId]?.tenancy ?? null, fullAddress: siteMetaByJobId[job.jobId]?.fullAddress ?? null, onViewJob: handleViewJob, onRequestChange: handleRequestChange, onCancellationRequest: handleCancellationRequest, onAlertClick: handleAlertClick, isDetailOpen: selectedJobId === job.id, selectedJobData: selectedJobId === job.id ? detailJobData : null, onCloseDetails: handleCloseDetails, onDecline: (job.isEncumbered ?? isEncumberedTrade(job.tradeType))
                                        ? handleRequestChange
                                        : handleCancellationRequest, isProcessing: processingId === job.id, pendingTradeDateChangeRequest: selectedJobId === job.id
                                        ? pendingTradeDateChangeRequestStaff ?? null
                                        : null, onViewTradeDateChangeDetails: selectedJobId === job.id
                                        ? () => setIsTradeDateChangeModalOpen(true)
                                        : undefined, hasPendingContractorDateChange: !!pendingContractorRequestsByAssignmentId[job.id] }, job.id)))) : (_jsx("div", { className: styles.emptyState, children: _jsx("p", { className: styles.emptyText, children: "No accepted jobs yet" }) })) }))] })] }), _jsx(DeclineReasonModal, { isOpen: declineModalAssignmentId !== null, onClose: () => {
                    if (processingId !== declineModalAssignmentId) {
                        handleCloseDeclineModal();
                    }
                }, onSubmit: handleDeclineWithReason, isSubmitting: processingId === declineModalAssignmentId, title: declineModalPurpose === "cancellation"
                    ? "Cancellation request"
                    : "Decline job" }), _jsx(TermsAndConditionsAcceptModal, { isOpen: acceptTcModalAssignmentId !== null, onClose: () => setAcceptTcModalAssignmentId(null), onAccept: async () => {
                    const id = acceptTcModalAssignmentId;
                    if (!id)
                        return;
                    await handleAccept(id, { termCondition: true });
                    setAcceptTcModalAssignmentId(null);
                }, isSubmitting: processingId === acceptTcModalAssignmentId }), _jsx(ChangeDateModal, { isOpen: isTradeDateChangeModalOpen, onClose: () => setIsTradeDateChangeModalOpen(false), onCommunicate: handleCommunicateWithStaff, communicateButtonLabel: "Communicate with TDG Staff", onApprove: handleApproveTradeDateChange, onDecline: handleDeclineTradeDateChange, dateChangeRequest: pendingTradeDateChangeRequestStaff
                    ? tradeDateChangeRequestToDisplay(pendingTradeDateChangeRequestStaff)
                    : null, formatDatesAsLocal: true }), _jsx(TradeDateChangeRequestModal, { isOpen: tradeDateChangeRequestJob !== null, onClose: () => setTradeDateChangeRequestJob(null), jobId: tradeDateChangeRequestJob?.jobId ?? "", assignment: tradeDateChangeRequestJob
                    ? toTradeAssignmentForModal(tradeDateChangeRequestJob)
                    : null, jobTradeId: tradeDateChangeRequestJob?.id, requestor: "Contractor", requestedById: user?.id, requestedByName: user?.name ?? undefined, onSuccess: () => {
                    fetchAssignments();
                    setTradeDateChangeRequestJob(null);
                } })] }));
};
